import type React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import type { Equipment } from 'app/types/graphql';
import Typography from 'app/ui/components/atoms/typography';
import { getEquipmentIcons } from 'app/ui/components/atoms/class-card/equipment-icons';
import Tick from 'app/ui/components/atoms/icons/tick';
import DotDivider from 'app/ui/components/atoms/dot-divider';

export type SimplifiedClassCardProps = {
  srcSet: string;
  title: React.ReactNode;
  to?: string;
  intensity?: string;
  equipment?: Equipment[] | null;
  label?: string;
  isDisabled?: boolean;
  isDone?: boolean;
  testId?: string;
  onClick?: () => void;
};

const CardContainer = styled.div<{ $isDisabled?: boolean; $isDone?: boolean; $isInteractive?: boolean }>`
  position: relative;
  width: 20rem;
  aspect-ratio: calc(420 / 260);
  border-radius: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.veryGray};
  cursor: ${({ $isDisabled, $isInteractive }) => ($isDisabled || !$isInteractive ? 'default' : 'pointer')};
  transition: border-width 0.1s ease-in-out, border-color 0.1s ease-in-out;
  box-sizing: border-box;

  &:hover {
    ${({ $isDisabled, $isInteractive, theme }) =>
      !$isDisabled &&
      $isInteractive &&
      css`
        border: 2px solid ${theme.colors.knop};
      `}
  }
`;

const ImageBackground = styled.div<{ $srcSet: string; $isDisabled?: boolean; $isDone?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${({ $srcSet }) => $srcSet});
  background-size: cover;
  background-position: center;
  ${({ $srcSet, theme }) => !$srcSet && `background-color: ${theme.colors.veryGray};`}

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.8) 100%);
    ${({ $isDisabled, $isDone }) =>
      ($isDisabled || $isDone) &&
      css`
        background: rgba(0, 0, 0, 0.6);
      `}
  }
`;

const ContentOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;
  box-sizing: border-box;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const LabelContainer = styled.div`
  background: white;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.spacing(1)};
`;

const LabelText = styled(Typography)`
  text-transform: uppercase;
  font-weight: 600;
  font-size: ${({ theme }) => theme.typography.tiny};
`;

const Title = styled(Typography)<{ $isDisabled?: boolean; $isDone?: boolean }>`
  font-weight: 600;
  color: ${({ theme, $isDisabled, $isDone }) =>
    $isDisabled || $isDone ? theme.colors.devilsGray : theme.colors.white};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  && {
    color: ${({ theme, $isDisabled, $isDone }) =>
      $isDisabled || $isDone ? theme.colors.devilsGray : theme.colors.white};
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RPEText = styled(Typography)<{ $isDisabled?: boolean; $isDone?: boolean }>`
  color: ${({ theme, $isDisabled, $isDone }) =>
    $isDisabled || $isDone ? theme.colors.devilsGray : theme.colors.white};

  && {
    color: ${({ theme, $isDisabled, $isDone }) =>
      $isDisabled || $isDone ? theme.colors.devilsGray : theme.colors.white};
  }
`;

const EqIcons = styled.div<{ $isDisabled?: boolean; $isDone?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  opacity: ${({ $isDisabled, $isDone }) => ($isDisabled || $isDone ? 0.5 : 1)};
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const DoneOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 2px solid ${({ theme }) => theme.colors.white};
  border-radius: 50%;
  width: ${({ theme }) => theme.spacing(16)};
  height: ${({ theme }) => theme.spacing(16)};

  svg {
    width: ${({ theme }) => theme.spacing(10)};
    height: ${({ theme }) => theme.spacing(10)};
  }
`;

const SimplifiedClassCard: React.FC<SimplifiedClassCardProps> = ({
  srcSet,
  title,
  to,
  intensity,
  equipment,
  label,
  isDisabled,
  isDone,
  testId,
  onClick,
}) => {
  const theme = useTheme();

  const isInteractive = (!!to || !!onClick) && !isDisabled;

  const handleCardClick = (e: React.MouseEvent) => {
    if (!isInteractive) {
      e.preventDefault();
      return;
    }
    onClick?.();
  };

  const isDimmed = isDisabled || isDone;

  const content = (
    <CardContainer
      $isDisabled={isDisabled}
      $isDone={isDone}
      $isInteractive={isInteractive}
      onClick={handleCardClick}
      data-testid={testId}
    >
      <ImageBackground $srcSet={srcSet} $isDisabled={isDisabled} $isDone={isDone} />
      {isDone && (
        <DoneOverlay>
          <Tick color={theme.colors.white} />
        </DoneOverlay>
      )}
      <ContentOverlay>
        <TopRow>
          {label && (
            <LabelContainer>
              <LabelText variant="label" color={theme.colors.black}>
                {label}
              </LabelText>
            </LabelContainer>
          )}
        </TopRow>
        <BottomRow>
          <Title variant="h5" $isDisabled={isDisabled} $isDone={isDone}>
            {title}
          </Title>
          <InfoRow>
            {intensity && (
              <RPEText variant="body2" $isDisabled={isDisabled} $isDone={isDone}>
                {intensity}
              </RPEText>
            )}
            {equipment && equipment.length > 0 && (
              <>
                <DotDivider color={isDimmed ? theme.colors.devilsGray : theme.colors.white} />
                <EqIcons $isDisabled={isDisabled} $isDone={isDone}>
                  {getEquipmentIcons(equipment, isDimmed ? theme.colors.devilsGray : theme.colors.white)}
                </EqIcons>
              </>
            )}
          </InfoRow>
        </BottomRow>
      </ContentOverlay>
    </CardContainer>
  );

  if (to && !isDisabled) {
    return <StyledLink to={to}>{content}</StyledLink>;
  }

  return content;
};

export default SimplifiedClassCard;
